/**
 * UI wiring helpers for the SharePoint Explorer. Centralises:
 *   1. The `Id<>` cast at the consumer boundary (callers pass generic
 *      `string` IDs; the cast happens once here).
 *   2. The "skip if id isn't a real Convex id" guard, so live queries
 *      never get fed mock ids (`c1`, `m1`, `d1`) and 422.
 *   3. The F10 Sample-workspace overlay: `useExplorerClientGroups`
 *      partitions a SINGLE live `clients.getMyClients` query on the
 *      `seeded:true` flag set by the F-FIRST-RUN-LIVE-DATA per-portal
 *      provisioner-seed path. Whether the showcase group is visible is
 *      decided by `useShowcaseVisible` in the consumer (not here).
 */

import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';

type AnyClientId = string;
type AnyMatterId = string;

function isLikelyConvexId(id: string): boolean {
  return id.length >= 16 && !/^[cmd]\d+$/.test(id);
}

export function useRepancyMatters(clientId: AnyClientId | null) {
  const valid = clientId !== null && isLikelyConvexId(clientId);
  return useQuery(
    api.matters.getMattersByClient,
    valid ? { clientId: clientId as Id<'clients'> } : 'skip',
  );
}

export function useRepancyDocuments(matterId: AnyMatterId | null) {
  const valid = matterId !== null && isLikelyConvexId(matterId);
  return useQuery(
    api.documents.getDocumentsByMatter,
    valid ? { matterId: matterId as Id<'matters'> } : 'skip',
  );
}

export function useRepancyClients() {
  return useQuery(api.clients.getMyClients, {});
}

/** F10.4 row shape consumed by the SharePoint Explorer's tree pane. */
export interface ExplorerClientRow {
  _id: string;
  name: string;
  industry: string;
  status: string;
  createdAt: string;
  /** True when this row came from the per-portal provisioner seed
   *  (F-FIRST-RUN-LIVE-DATA). The Explorer renders these inside the
   *  "Sample workspace" group; user-created rows render in
   *  "Your portal". */
  seeded?: boolean;
}

export interface ExplorerClientGroups {
  /** User-created clients (seeded:false / unset). `rows` is undefined while loading. */
  live: { rows: ExplorerClientRow[] | undefined };
  /** Sample-workspace clients (seeded:true). `rows` is undefined while loading. */
  showcase: { rows: ExplorerClientRow[] | undefined };
}

/**
 * Single `clients.getMyClients` query, partitioned on the `seeded`
 * boolean into the two-group layout TreePane consumes. Replaces the
 * pre-F-FIRST-RUN-LIVE-DATA dual-subscription that pulled from a
 * separate `demoClients` Convex table in parallel.
 */
export function useExplorerClientGroups(): ExplorerClientGroups {
  const all = useQuery(api.clients.getMyClients, {}) as
    | ExplorerClientRow[]
    | undefined;
  if (all === undefined) {
    return {
      live: { rows: undefined },
      showcase: { rows: undefined },
    };
  }
  const live: ExplorerClientRow[] = [];
  const showcase: ExplorerClientRow[] = [];
  for (const c of all) {
    if (c.seeded === true) {
      showcase.push(c);
    } else {
      live.push(c);
    }
  }
  return {
    live: { rows: live },
    showcase: { rows: showcase },
  };
}
